import InboxesAPI from 'dashboard/api/inboxes';

const DEFAULT_MEDIA_CONSTRAINTS = { audio: true, video: false };
const ICE_GATHERING_TIMEOUT_MS = 3000;
export const WHATSAPP_CALL_AUDIO_EVENTS = {
  PLAYING: 'airys:whatsapp-call-audio-playing',
  BLOCKED: 'airys:whatsapp-call-audio-blocked',
};

const sessionDescription = description => {
  if (typeof RTCSessionDescription === 'undefined') return description;
  return new RTCSessionDescription(description);
};

const dispatchAudioEvent = (eventName, detail) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(eventName, { detail }));
};

const playRemoteAudio = (peerConnection, audio) => {
  if (!audio.play) return;

  Promise.resolve(audio.play())
    .then(() => {
      dispatchAudioEvent(WHATSAPP_CALL_AUDIO_EVENTS.PLAYING, {
        callId: peerConnection.airysCallId,
      });
    })
    .catch(error => {
      peerConnection.airysRemoteAudioPlaybackError = {
        name: error?.name,
        message: error?.message,
      };
      dispatchAudioEvent(WHATSAPP_CALL_AUDIO_EVENTS.BLOCKED, {
        callId: peerConnection.airysCallId,
        error: peerConnection.airysRemoteAudioPlaybackError,
      });
    });
};

const waitForIceGatheringComplete = peerConnection => {
  if (peerConnection.iceGatheringState === 'complete') {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    let timeoutId;
    let onStateChange;
    const done = () => {
      clearTimeout(timeoutId);
      peerConnection.removeEventListener?.(
        'icegatheringstatechange',
        onStateChange
      );
      resolve();
    };
    onStateChange = () => {
      if (peerConnection.iceGatheringState === 'complete') done();
    };

    timeoutId = setTimeout(done, ICE_GATHERING_TIMEOUT_MS);
    peerConnection.addEventListener?.('icegatheringstatechange', onStateChange);
  });
};

class WhatsAppCallingClient {
  constructor({
    peerConnectionFactory = () => new RTCPeerConnection(),
    mediaDevices = typeof navigator !== 'undefined'
      ? navigator.mediaDevices
      : null,
    remoteAudioFactory = () => {
      if (typeof document === 'undefined') return null;

      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.playsInline = true;
      audio.setAttribute('aria-hidden', 'true');
      audio.style.position = 'fixed';
      audio.style.width = '1px';
      audio.style.height = '1px';
      audio.style.opacity = '0';
      audio.style.pointerEvents = 'none';
      audio.style.left = '-9999px';
      document.body.appendChild(audio);
      return audio;
    },
  } = {}) {
    this.peerConnectionFactory = peerConnectionFactory;
    this.mediaDevices = mediaDevices;
    this.remoteAudioFactory = remoteAudioFactory;
    this.sessions = new Map();
  }

  isSupported() {
    return (
      typeof RTCPeerConnection !== 'undefined' &&
      !!this.mediaDevices?.getUserMedia
    );
  }

  async initiateOutboundCall({
    inboxId,
    contactId,
    to,
    recipient,
    bizOpaqueCallbackData,
  }) {
    const localSession = await this.createLocalOffer();
    const { data } = await InboxesAPI.initiateCalling(inboxId, {
      contact_id: contactId,
      to,
      recipient,
      sdp: localSession.sdp,
      biz_opaque_callback_data: bizOpaqueCallbackData,
    });
    this.rememberSession(data, localSession);
    return data;
  }

  async acceptInboundCall({
    inboxId,
    callId,
    remoteSdp,
    bizOpaqueCallbackData,
    preAccept = true,
  }) {
    const localSession = await this.createLocalAnswer(remoteSdp, callId);
    if (preAccept) {
      await InboxesAPI.preAcceptCall(inboxId, {
        call_id: callId,
        sdp: localSession.sdp,
      });
    }
    const { data } = await InboxesAPI.acceptCall(inboxId, {
      call_id: callId,
      sdp: localSession.sdp,
      biz_opaque_callback_data: bizOpaqueCallbackData,
    });
    this.rememberSession(data, localSession, callId);
    return data;
  }

  async connectOutboundCall({ callId, remoteSdp }) {
    if (!callId || !remoteSdp) return false;

    const session = this.sessions.get(callId);
    const peerConnection = session?.peerConnection;
    if (!peerConnection || peerConnection.remoteDescription) return false;

    await peerConnection.setRemoteDescription(
      sessionDescription({ type: 'answer', sdp: remoteSdp })
    );
    return true;
  }

  async resumeRemoteAudio(callId) {
    const session = this.sessions.get(callId);
    const audio =
      session?.remoteAudio || session?.peerConnection?.airysRemoteAudio;
    if (!audio?.play) return false;

    await audio.play();
    return true;
  }

  async preAcceptInboundCall({ inboxId, callId, remoteSdp }) {
    const localSession = await this.createLocalAnswer(remoteSdp, callId);
    const { data } = await InboxesAPI.preAcceptCall(inboxId, {
      call_id: callId,
      sdp: localSession.sdp,
    });
    this.rememberSession(data, localSession, callId);
    return data;
  }

  async rejectCall({ inboxId, callId }) {
    const { data } = await InboxesAPI.rejectCall(inboxId, { call_id: callId });
    this.closeSession(callId);
    return data;
  }

  async terminateCall({ inboxId, callId }) {
    const { data } = await InboxesAPI.terminateCall(inboxId, {
      call_id: callId,
    });
    this.closeSession(callId);
    return data;
  }

  async createLocalOffer() {
    const peerConnection = this.createPeerConnection();
    const stream = await this.attachLocalAudio(peerConnection);
    const offer = await peerConnection.createOffer({
      offerToReceiveAudio: true,
    });
    await peerConnection.setLocalDescription(offer);
    await waitForIceGatheringComplete(peerConnection);

    return {
      peerConnection,
      stream,
      sdp: peerConnection.localDescription?.sdp || offer.sdp,
    };
  }

  async createLocalAnswer(remoteSdp, callId = null) {
    if (!remoteSdp) throw new Error('Remote SDP offer is required.');

    const peerConnection = this.createPeerConnection();
    peerConnection.airysCallId = callId;
    const stream = await this.attachLocalAudio(peerConnection);
    await peerConnection.setRemoteDescription(
      sessionDescription({ type: 'offer', sdp: remoteSdp })
    );
    const answer = await peerConnection.createAnswer();
    await peerConnection.setLocalDescription(answer);
    await waitForIceGatheringComplete(peerConnection);

    return {
      peerConnection,
      stream,
      sdp: peerConnection.localDescription?.sdp || answer.sdp,
    };
  }

  createPeerConnection() {
    if (!this.isSupported()) {
      throw new Error('WhatsApp Calling is not supported in this browser.');
    }
    const peerConnection = this.peerConnectionFactory();
    const attachRemoteStream = event => {
      const [stream] = event.streams || [];
      if (!stream) return;

      this.attachRemoteAudio(peerConnection, stream);
    };
    if (peerConnection.addEventListener) {
      peerConnection.addEventListener('track', attachRemoteStream);
    } else {
      peerConnection.ontrack = attachRemoteStream;
    }
    return peerConnection;
  }

  attachRemoteAudio(peerConnection, stream) {
    const audio = this.remoteAudioFactory?.();
    if (!audio) return;

    audio.srcObject = stream;
    peerConnection.airysRemoteAudio = audio;
    playRemoteAudio(peerConnection, audio);
  }

  async attachLocalAudio(peerConnection) {
    const stream = await this.mediaDevices.getUserMedia(
      DEFAULT_MEDIA_CONSTRAINTS
    );
    stream.getTracks().forEach(track => peerConnection.addTrack(track, stream));
    return stream;
  }

  rememberSession(response, localSession, fallbackCallId = null) {
    const callId =
      response?.call_session?.call_id ||
      response?.call?.id ||
      response?.call?.call_id ||
      fallbackCallId;
    if (!callId) return;

    if (localSession?.peerConnection) {
      localSession.peerConnection.airysCallId = callId;
    }
    this.sessions.set(callId, localSession);
  }

  closeSession(callId) {
    const session = this.sessions.get(callId);
    session?.stream?.getTracks?.().forEach(track => track.stop());
    const remoteAudio =
      session?.remoteAudio || session?.peerConnection?.airysRemoteAudio;
    remoteAudio?.remove?.();
    session?.peerConnection?.close?.();
    this.sessions.delete(callId);
  }
}

export { WhatsAppCallingClient };
export default new WhatsAppCallingClient();
